import { useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import GuideDialog from "@/components/GuideDialog";
import { INSIGHTS, getInsight } from "@/data/insights";
import { useStudio } from "@/context/StudioContext";
import { useSEO, breadcrumb, faqPage, SITE } from "@/lib/useSEO";

/**
 * InsightArticle — /insights/:slug
 * Long-form editorial article with Article + BreadcrumbList + FAQPage JSON-LD.
 */
export default function InsightArticle() {
  const { slug } = useParams();
  const { openBooking } = useStudio();
  const [guideOpen, setGuideOpen] = useState(false);

  const article = useMemo(() => getInsight(slug), [slug]);

  const related = useMemo(
    () => (article ? INSIGHTS.filter((a) => a.slug !== article.slug).slice(0, 2) : []),
    [article]
  );

  const canonicalPath = article ? `/insights/${article.slug}` : "/insights";
  const url = `${SITE}${canonicalPath}`;
  const imageAbs = article ? `${SITE}${article.heroImage}` : undefined;

  const articleSchema = article && {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline: article.title,
    alternativeHeadline: article.subtitle,
    description: article.excerpt,
    image: imageAbs,
    datePublished: article.dateISO,
    dateModified: article.dateISO,
    articleSection: article.category,
    author: { "@type": "Organization", name: "My Floor Studio", url: SITE },
    publisher: {
      "@type": "Organization",
      name: "My Floor Studio",
      logo: { "@type": "ImageObject", url: `${SITE}/logo/logo-primary.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  const breadcrumbSchema = breadcrumb([
    { name: "Home", path: "/" },
    { name: "Journal", path: "/insights" },
    { name: article ? article.title : "Article", path: canonicalPath },
  ]);

  const faqSchema = article && article.faqs?.length ? faqPage(article.faqs) : null;

  useSEO({
    title: article
      ? `${article.title} — My Floor Studio Journal`
      : "Journal — My Floor Studio",
    description: article?.excerpt,
    path: canonicalPath,
    keywords: article?.keywords,
    image: imageAbs,
    imageAlt: article?.heroAlt,
    type: "article",
    jsonLd: [articleSchema, breadcrumbSchema, faqSchema].filter(Boolean),
  });

  if (!article) return <Navigate to="/insights" replace />;

  return (
    <div className="min-h-screen bg-[#121315] grain">
      <Nav />
      <main role="main" className="pt-24 md:pt-28">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="px-6 md:px-12 py-4 text-[11px] tracking-[0.2em] uppercase text-[#D6D1CA]/55"
        >
          <Link to="/" className="hover:text-[#C9A876]">Home</Link>
          <span className="mx-3">/</span>
          <Link to="/insights" className="hover:text-[#C9A876]">Journal</Link>
          <span className="mx-3">/</span>
          <span className="text-[#F5F4F0]/85">{article.title}</span>
        </nav>

        {/* Header */}
        <header className="px-6 md:px-12 pt-4 pb-10 max-w-3xl">
          <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-5">
            {article.category} · {article.readMins} min read · {article.date}
          </p>
          <h1 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-4xl sm:text-5xl md:text-6xl leading-[1.0]">
            {article.title}
          </h1>
          <p className="mt-5 text-[#D6D1CA]/75 text-lg md:text-xl font-body leading-relaxed">
            {article.subtitle}
          </p>
          {article.pdf && (
            <button
              type="button"
              onClick={() => setGuideOpen(true)}
              data-testid="guide-download-btn-header"
              className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#8C7355]/50 px-6 py-3 text-[10px] tracking-[0.25em] uppercase text-[#C9A876] hover:border-[#C9A876] hover:text-[#dcb884]"
              style={{ transition: "color .4s ease, border-color .4s ease" }}
            >
              <Download size={13} strokeWidth={1.8} />
              Download as PDF
            </button>
          )}
        </header>

        {/* Hero image */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="px-6 md:px-12"
        >
          <div className="aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden rounded-sm bg-[#0f1012]">
            <img
              src={article.heroImage}
              alt={article.heroAlt}
              className="w-full h-full object-cover"
              fetchPriority="high"
            />
          </div>
        </motion.div>

        {/* Body */}
        <article className="px-6 md:px-12 py-16 md:py-24 max-w-3xl">
          {article.sections.map((s, i) => (
            <section key={i} className="mb-12 last:mb-0">
              {s.heading && (
                <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-2xl md:text-3xl leading-tight mb-5">
                  {s.heading}
                </h2>
              )}
              {(s.paragraphs || []).map((p, pi) => (
                <p
                  key={pi}
                  className="text-[#D6D1CA]/80 text-[16px] md:text-[17px] font-body leading-[1.85] mb-5"
                >
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-2 mb-2 space-y-3">
                  {s.list.map((li, li2) => (
                    <li
                      key={li2}
                      className="flex gap-4 text-[#D6D1CA]/80 text-[15px] md:text-[16px] font-body leading-[1.7]"
                    >
                      <span aria-hidden className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-[#C9A876]" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.callout && (
                <blockquote className="mt-6 border-l-2 border-[#C9A876] pl-6 py-1 text-[#F5F4F0]/90 text-lg md:text-xl font-serif-display italic leading-snug">
                  {s.callout}
                </blockquote>
              )}
            </section>
          ))}
        </article>

        {/* FAQs */}
        {article.faqs?.length > 0 && (
          <section className="px-6 md:px-12 py-16 md:py-24 border-t border-[#8C7355]/15">
            <div className="max-w-3xl">
              <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-4">
                Frequently asked
              </p>
              <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-4xl leading-tight mb-10">
                Quick answers.
              </h2>
              <dl className="space-y-8">
                {article.faqs.map((f, i) => (
                  <div key={i}>
                    <dt className="text-[#F5F4F0]/90 font-body text-lg mb-2">{f.q}</dt>
                    <dd className="text-[#D6D1CA]/70 font-body text-[15px] leading-[1.8]">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="px-6 md:px-12 py-16 md:py-20 border-t border-[#8C7355]/15">
          <div className="max-w-3xl">
            {article.pdf && (
              <div className="mb-12 border border-[#8C7355]/25 bg-[#1a1b1e]/50 p-7 md:p-9">
                <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase">Take it with you</p>
                <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-2xl md:text-3xl mt-3">
                  {article.title}, as a PDF
                </h2>
                <p className="text-[#D6D1CA]/70 text-sm font-body mt-3 max-w-xl leading-relaxed">
                  A designed, printable version of this guide — keep it, mark it up, or send it
                  straight to your builder or client.
                </p>
                <button
                  type="button"
                  onClick={() => setGuideOpen(true)}
                  data-testid="guide-download-btn"
                  className="btn-bronze inline-flex items-center gap-2 rounded-full px-7 py-3.5 mt-6 text-[11px] tracking-[0.22em] uppercase font-medium"
                >
                  <Download size={13} strokeWidth={1.8} />
                  Get the PDF
                </button>
              </div>
            )}
            <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-4xl leading-tight">
              Specifying a project?
            </h2>
            <p className="text-[#D6D1CA]/75 text-lg font-body mt-4 leading-relaxed">
              Book a studio appointment and we'll work through the right floor for every room —
              and organise samples for your project.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openBooking}
                data-testid="insight-book-appointment-btn"
                className="inline-block rounded-full bg-[#C9A876] px-7 py-3 text-[11px] tracking-[0.25em] uppercase text-[#121315] hover:bg-[#dcb884] transition-colors"
              >
                Request appointment
              </button>
              <Link
                to="/collection"
                className="inline-block rounded-full border border-[#8C7355]/50 px-7 py-3 text-[11px] tracking-[0.25em] uppercase text-[#F5F4F0]/90 hover:border-[#C9A876] transition-colors"
              >
                Browse the collection
              </Link>
              {article.element && (
                <Link
                  to={`/${article.element.toLowerCase()}`}
                  data-testid={`insight-element-link-${article.element.toLowerCase()}`}
                  className="inline-block rounded-full border border-[#8C7355]/50 px-7 py-3 text-[11px] tracking-[0.25em] uppercase text-[#F5F4F0]/90 hover:border-[#C9A876] transition-colors"
                >
                  Explore {article.element}
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="px-6 md:px-12 py-16 md:py-24 border-t border-[#8C7355]/15">
            <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-8">Keep reading</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {related.map((r) => (
                <Link key={r.slug} to={`/insights/${r.slug}`} className="group block">
                  <div className="aspect-[16/10] overflow-hidden rounded-sm bg-[#0f1012]">
                    <img
                      src={r.heroImage}
                      alt={r.heroAlt}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                    />
                  </div>
                  <p className="mt-4 text-[10px] tracking-[0.3em] uppercase text-[#C9A876]">
                    {r.category} · {r.readMins} min
                  </p>
                  <h3 className="font-serif-display italic text-[#F5F4F0] text-2xl mt-1">{r.title}</h3>
                  <p className="text-[#D6D1CA]/60 text-sm mt-1 font-body">{r.subtitle}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <GuideDialog open={guideOpen} onOpenChange={setGuideOpen} guide={article} />
      <Footer />
    </div>
  );
}
